/**
 * STL API - Handles all STL file operations
 * Extracted from backendService.ts to improve modularity
 */

import { logInfo, logError, logAPIError, logWarning } from '../../utils/errorLogger'
import type {
  STLFileResponse,
  StlInStatusResponse,
  StlFileRole,
  LandmarksResponse
} from '../types'

/**
 * Upload STL file
 */
export async function uploadSTLFile(
  baseURL: string,
  authToken: string | null,
  timeout: number,
  file: File,
  projectId?: string,
  patientId?: string,
  doctorId?: string,
  originalBoundingBox?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } },
  fileType: StlFileRole = 'stl_in'
): Promise<STLFileResponse> {
  const requestId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  try {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)

    logInfo(`📤 [${requestId}] uploadSTLFile START`, 'Backend')
    logInfo(`📤 [${requestId}] File details:`, 'Backend')
    logInfo(`📤 [${requestId}] - Name: ${file.name}`, 'Backend')
    logInfo(`📤 [${requestId}] - Size: ${file.size} bytes (${fileSizeMB} MB)`, 'Backend')
    logInfo(`📤 [${requestId}] - Type: ${file.type}`, 'Backend')
    logInfo(`📤 [${requestId}] - Last Modified: ${new Date(file.lastModified).toISOString()}`, 'Backend')
    logInfo(`📤 [${requestId}] Parameters:`, 'Backend')
    logInfo(`📤 [${requestId}] - Project ID: ${projectId || 'undefined'}`, 'Backend')
    logInfo(`📤 [${requestId}] - Patient ID: ${patientId || 'undefined'}`, 'Backend')
    logInfo(`📤 [${requestId}] - Doctor ID: ${doctorId || 'undefined'}`, 'Backend')
    logInfo(`📤 [${requestId}] - File Type: ${fileType}`, 'Backend')
    if (originalBoundingBox) {
      logInfo(`📤 [${requestId}] - Original Bounding Box: ${JSON.stringify(originalBoundingBox.size)}`, 'Backend')
    }

    const formData = new FormData()
    formData.append('file', file)
    if (projectId) formData.append('projectId', projectId)
    if (patientId) formData.append('patientId', patientId)
    if (doctorId) formData.append('doctorId', doctorId)
    if (originalBoundingBox) {
      formData.append('originalBoundingBox', JSON.stringify(originalBoundingBox))
    }
    formData.append('fileType', fileType)

    logInfo(`📤 [${requestId}] FormData prepared with ${formData.has('file') ? 'file' : 'NO FILE'} and ${Array.from(formData.keys()).length} total fields`, 'Backend')

    const url = `${baseURL}/api/stl/upload`
    const headers: { [key: string]: string } = {}
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
      logInfo(`📤 [${requestId}] Auth token added: ${authToken.substring(0, 20)}...`, 'Backend')
    } else {
      logWarning(`📤 [${requestId}] No auth token available`, 'Backend')
    }

    logInfo(`📤 [${requestId}] Request details:`, 'Backend')
    logInfo(`📤 [${requestId}] - URL: ${url}`, 'Backend')
    logInfo(`📤 [${requestId}] - Method: POST`, 'Backend')
    logInfo(`📤 [${requestId}] - Headers: ${JSON.stringify(headers, null, 2)}`, 'Backend')
    logInfo(`📤 [${requestId}] - Timeout: ${(timeout * 6) / 1000} seconds`, 'Backend')

    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      logError(`⏰ [${requestId}] Upload timeout after ${(timeout * 6) / 1000} seconds`, 'Backend')
      controller.abort()
    }, timeout * 6) // Much longer timeout for large STL files (3 minutes)

    const startTime = Date.now()
    logInfo(`📤 [${requestId}] Starting fetch request...`, 'Backend')

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
      signal: controller.signal
    })

    clearTimeout(timeoutId)
    const requestDuration = Date.now() - startTime

    logInfo(`📤 [${requestId}] Fetch completed (${requestDuration}ms)`, 'Backend')
    logInfo(`📤 [${requestId}] Response details:`, 'Backend')
    logInfo(`📤 [${requestId}] - Status: ${response.status} ${response.statusText}`, 'Backend')
    logInfo(`📤 [${requestId}] - Headers: ${JSON.stringify(Object.fromEntries(response.headers.entries()), null, 2)}`, 'Backend')
    logInfo(`📤 [${requestId}] - OK: ${response.ok}`, 'Backend')

    if (!response.ok) {
      logError(`❌ [${requestId}] HTTP error response: ${response.status} ${response.statusText}`, 'Backend')
      let errorData = {}
      try {
        errorData = await response.json()
        logError(`❌ [${requestId}] Error response body: ${JSON.stringify(errorData, null, 2)}`, 'Backend')
      } catch (parseError) {
        logError(`❌ [${requestId}] Failed to parse error response: ${parseError}`, 'Backend')
      }
      throw new Error((errorData as any).message || `HTTP ${response.status}: ${response.statusText}`)
    }

    logInfo(`📤 [${requestId}] Parsing successful response...`, 'Backend')
    const result = await response.json() as STLFileResponse

    logInfo(`✅ [${requestId}] uploadSTLFile SUCCESS (${requestDuration}ms total)`, 'Backend')
    logInfo(`✅ [${requestId}] Upload result: ${JSON.stringify(result, null, 2)}`, 'Backend')

    return result
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to upload STL file'
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)

    // Enhanced error logging with more context
    if (error instanceof Error && error.name === 'AbortError') {
      const timeoutMessage = `STL upload timeout after ${(timeout * 6) / 1000} seconds for file ${file.name} (${fileSizeMB} MB)`
      logAPIError('/api/stl/upload', 'POST', 0, timeoutMessage, { fileName: file.name, fileSize: file.size, timeout: true })
      throw new Error(`Upload timeout: File ${file.name} (${fileSizeMB} MB) is too large or connection is slow. Please try again or use a smaller file.`)
    }

    logAPIError('/api/stl/upload', 'POST', 0, errorMessage, { fileName: file.name, fileSize: file.size, fileSizeMB })
    throw error
  }
}

/**
 * Upload STL file with XHR for real upload progress tracking
 */
export async function uploadSTLFileWithProgress(
  baseURL: string,
  authToken: string | null,
  file: File,
  projectId?: string,
  patientId?: string,
  doctorId?: string,
  originalBoundingBox?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } },
  fileType: StlFileRole = 'stl_in',
  onProgress?: (progress: { percentage: number; bytesUploaded: number; totalBytes: number }) => void
): Promise<STLFileResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (projectId) formData.append('projectId', projectId)
  if (patientId) formData.append('patientId', patientId)
  if (doctorId) formData.append('doctorId', doctorId)
  if (originalBoundingBox) formData.append('originalBoundingBox', JSON.stringify(originalBoundingBox))
  formData.append('fileType', fileType)

  const url = `${baseURL}/api/stl/upload`

  return new Promise<STLFileResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({ percentage: Math.round((e.loaded / e.total) * 100), bytesUploaded: e.loaded, totalBytes: e.total })
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as STLFileResponse)
        } catch {
          reject(new Error('Failed to parse upload response'))
        }
      } else {
        let msg = `HTTP ${xhr.status}`
        try { msg = (JSON.parse(xhr.responseText) as any).message || msg } catch { /* ignore */ }
        reject(new Error(msg))
      }
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.open('POST', url)
    xhr.withCredentials = true  // Send auth cookie (#99)
    if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)
    xhr.send(formData)
  })
}

/**
 * Get STL file status
 */
export async function getStlStatus(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  authToken: string | null,
  projectId: string,
  fileRole: StlFileRole = 'stl_in'
): Promise<StlInStatusResponse> {
  const requestId = `status-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  const routeSegment = fileRole === 'stl_out' ? 'stl-out' : 'stl-in'
  try {
    if (!projectId) {
      logError(`❌ [${requestId}] getStlStatus failed: projectId is required`, 'Backend')
      throw new Error('projectId is required')
    }

    logInfo(`📡 [${requestId}] getStlStatus START - Project: ${projectId}, Role: ${fileRole}`, 'Backend')
    logInfo(`📡 [${requestId}] Request URL: /api/projects/${projectId}/${routeSegment}/status`, 'Backend')

    // Auth presence check only. The token is treated as opaque on the
    // client — the backend is the sole authority on validity (#104).
    // Note: cookie-based sessions (httpOnly) may have null authToken in memory
    // after page reload — the cookie is sent automatically via credentials:include.
    if (!authToken) {
      logInfo(`📡 [${requestId}] No in-memory token; relying on httpOnly cookie`, 'Backend')
    }

    const startTime = Date.now()

    // Add cache-busting parameter to prevent stale responses
    const cacheBuster = `?_t=${Date.now()}`
    const response = await makeRequest<StlInStatusResponse>(`/api/projects/${projectId}/${routeSegment}/status${cacheBuster}`)
    const duration = Date.now() - startTime

    logInfo(`✅ [${requestId}] getStlStatus SUCCESS (${duration}ms)`, 'Backend')
    logInfo(`✅ [${requestId}] Response: ${JSON.stringify(response, null, 2)}`, 'Backend')

    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get STL status'
    logError(`❌ [${requestId}] getStlStatus FAILED: ${errorMessage}`, 'Backend')

    // Enhanced error context
    const errorContext = {
      projectId,
      requestId,
      hasAuthToken: !!authToken,
      authTokenLength: authToken?.length || 0,
      errorType: error instanceof Error ? error.constructor.name : 'Unknown',
      timestamp: new Date().toISOString(),
      stack: error instanceof Error ? error.stack : undefined
    }

    logAPIError(`/api/projects/${projectId}/${routeSegment}/status`, 'GET', 0, errorMessage, errorContext)
    throw error
  }
}

/**
 * Delete STL file(s) for a project
 */
export async function deleteSTLFile(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  projectId: string,
  fileRole: StlFileRole = 'stl_in'
): Promise<any> {
  try {
    const routeSegment = fileRole === 'stl_out' ? 'stl-out' : 'stl-in'
    logInfo(`Deleting STL file for project: ${projectId}`, 'Backend')
    const response = await makeRequest<any>(`/api/projects/${projectId}/${routeSegment}`, { method: 'DELETE' })
    logInfo(`STL file deleted successfully for project: ${projectId}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete STL file'
    const routeSegment = fileRole === 'stl_out' ? 'stl-out' : 'stl-in'
    logAPIError(`/api/projects/${projectId}/${routeSegment}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

/**
 * Get STL file metadata
 */
export async function getSTLMetadata(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  fileId: string
): Promise<STLFileResponse> {
  try {
    logInfo(`Getting STL metadata for file: ${fileId}`, 'Backend')
    const response = await makeRequest<STLFileResponse>(`/api/stl/${fileId}`)
    logInfo(`STL metadata retrieved: ${response.originalName}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get STL metadata'
    logAPIError(`/api/stl/${fileId}`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Save calibration points for an STL file
 */
export async function saveCalibration(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  fileId: string,
  calibrationPoints: {
    heel: { x: number; y: number; z: number }
    mt1: { x: number; y: number; z: number }
    mt5: { x: number; y: number; z: number }
    invert_front_back?: boolean
    invert_top_bottom?: boolean
  }
): Promise<{ success: boolean; message: string }> {
  try {
    logInfo(`Saving calibration for STL file: ${fileId}`, 'Backend')
    const response = await makeRequest<{ success: boolean; message: string }>(
      `/api/stl/${fileId}/calibration`,
      {
        method: 'POST',
        body: JSON.stringify(calibrationPoints)
      }
    )
    logInfo(`Calibration saved successfully for file: ${fileId}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to save calibration'
    logAPIError(`/api/stl/${fileId}/calibration`, 'POST', 0, errorMessage)
    throw error
  }
}

/**
 * Get calibration data for an STL file
 */
export async function getCalibration(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  fileId: string
): Promise<{
  calibrated: boolean
  calibrationPoints?: {
    heel: { x: number; y: number; z: number }
    mt1: { x: number; y: number; z: number }
    mt5: { x: number; y: number; z: number }
  }
  orientationMatrix?: number[][]
}> {
  try {
    logInfo(`Getting calibration for STL file: ${fileId}`, 'Backend')
    const response = await makeRequest<any>(`/api/stl/${fileId}/calibration`)
    logInfo(`Calibration retrieved for file: ${fileId}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get calibration'
    logAPIError(`/api/stl/${fileId}/calibration`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Get foot measurements for a calibrated STL file
 */
export async function getMeasurements(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  fileId: string
): Promise<{
  calibrated: boolean
  measurements?: {
    foot_length: number
    foot_width: number
    arch_height: number
    arch_classification: string
    heel_width: number
    instep_height: number
    units: string
  }
  message?: string
}> {
  try {
    logInfo(`Getting measurements for STL file: ${fileId}`, 'Backend')
    const response = await makeRequest<any>(`/api/stl/${fileId}/measurements`)
    logInfo(`Measurements retrieved for file: ${fileId}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get measurements'
    logAPIError(`/api/stl/${fileId}/measurements`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Get landmarks for an STL file
 */
export async function getLandmarks(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  fileId: string,
  filters?: {
    landmark_type?: string
    detection_method?: string
    min_confidence?: number
  }
): Promise<LandmarksResponse> {
  try {
    logInfo(`Getting landmarks for STL file: ${fileId}`, 'Backend')

    const params = new URLSearchParams()
    if (filters?.landmark_type) params.append('landmark_type', filters.landmark_type)
    if (filters?.detection_method) params.append('detection_method', filters.detection_method)
    if (filters?.min_confidence !== undefined) params.append('min_confidence', filters.min_confidence.toString())

    const url = `/api/stl/${fileId}/landmarks${params.toString() ? '?' + params.toString() : ''}`
    const response = await makeRequest<LandmarksResponse>(url)
    logInfo(`Landmarks retrieved for file: ${fileId} (${response.total_count} landmarks)`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to get landmarks'
    logAPIError(`/api/stl/${fileId}/landmarks`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Get STL download URL for a project
 */
export function getSTLDownloadUrl(baseURL: string, projectId: string, fileRole: StlFileRole = 'stl_in'): string {
  const routeSegment = fileRole === 'stl_out' ? 'stl-out' : 'stl-in'
  return `${baseURL}/api/projects/${projectId}/${routeSegment}/download`
}
